import React, { useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  AffiliateDetail,
  affiliateAdminApi,
  apiErrorMessage,
} from "./affiliateAdminApi";
import { StatusChip, affiliateQueryKeys } from "./AffiliatesAdminPage";
import { BrandingEditor } from "./BrandingEditor";

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
      <Typography variant="h6" component="h2">
        {title}
      </Typography>
      {description && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {description}
        </Typography>
      )}
      {children}
    </Paper>
  );
}

function useInvalidateAffiliate(affiliateId: number) {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      queryClient.invalidateQueries({
        queryKey: affiliateQueryKeys.detail(affiliateId),
      }),
      queryClient.invalidateQueries({ queryKey: affiliateQueryKeys.list }),
    ]);
}

function SettingsSection({ detail }: { detail: AffiliateDetail }) {
  const invalidate = useInvalidateAffiliate(detail.affiliateId);
  const [name, setName] = useState(detail.name);
  const [hostname, setHostname] = useState(detail.hostname ?? "");

  const save = useMutation({
    mutationFn: () =>
      affiliateAdminApi.update(detail.affiliateId, {
        name: name.trim(),
        // Sent even when empty: that clears the hostname.
        hostname: hostname.trim(),
      }),
    onSuccess: invalidate,
  });

  const unchanged =
    name.trim() === detail.name && hostname.trim() === (detail.hostname ?? "");

  return (
    <Section title="Settings">
      {save.isError && (
        <Alert severity="error" role="alert" sx={{ mb: 2 }}>
          {apiErrorMessage(save.error)}
        </Alert>
      )}

      <Stack spacing={2} sx={{ maxWidth: 520 }}>
        <TextField
          label="Name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          required
          slotProps={{ htmlInput: { maxLength: 100 } }}
        />
        <TextField
          label="Hostname"
          placeholder="brand.example.com"
          value={hostname}
          onChange={(event) => setHostname(event.target.value)}
          helperText="Leave empty to reach this affiliate only through ?affiliate= links."
          slotProps={{
            htmlInput: { autoCapitalize: "none", spellCheck: false },
          }}
        />
        <Box>
          <Button
            variant="outlined"
            disabled={unchanged || !name.trim() || save.isPending}
            onClick={() => save.mutate()}
          >
            Save
          </Button>
        </Box>
      </Stack>
    </Section>
  );
}

function StatusSection({ detail }: { detail: AffiliateDetail }) {
  const invalidate = useInvalidateAffiliate(detail.affiliateId);
  const [confirmSuspend, setConfirmSuspend] = useState(false);
  const hasBranding = detail.activeBrandingVersion != null;

  const updateStatus = useMutation({
    mutationFn: (status: AffiliateDetail["status"]) =>
      affiliateAdminApi.update(detail.affiliateId, { status }),
    onSuccess: () => {
      setConfirmSuspend(false);
      return invalidate();
    },
  });

  return (
    <Section
      title="Status"
      description="Only active affiliates get branded sign-in. A suspended affiliate's hostname falls back to the standard sign-in page."
    >
      {updateStatus.isError && (
        <Alert severity="error" role="alert" sx={{ mb: 2 }}>
          {apiErrorMessage(updateStatus.error)}
        </Alert>
      )}
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
      >
        <StatusChip status={detail.status} />
        {detail.status === "active" ? (
          <Button
            color="warning"
            variant="outlined"
            onClick={() => setConfirmSuspend(true)}
          >
            Suspend
          </Button>
        ) : (
          <Button
            variant="contained"
            disabled={!hasBranding || updateStatus.isPending}
            onClick={() => updateStatus.mutate("active")}
          >
            Activate
          </Button>
        )}
        {detail.status !== "active" && !hasBranding && (
          <Typography variant="body2" color="text.secondary">
            Publish branding before activating.
          </Typography>
        )}
      </Stack>

      <Dialog open={confirmSuspend} onClose={() => setConfirmSuspend(false)}>
        <DialogTitle>Suspend {detail.name}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Their sign-in page reverts to the standard one. Existing users can
            still sign in.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmSuspend(false)}>Cancel</Button>
          <Button
            color="warning"
            variant="contained"
            disabled={updateStatus.isPending}
            onClick={() => updateStatus.mutate("suspended")}
          >
            Suspend
          </Button>
        </DialogActions>
      </Dialog>
    </Section>
  );
}

export default function AffiliateDetailPage() {
  const params = useParams();
  const affiliateId = Number(params.id);
  const validId = Number.isInteger(affiliateId) && affiliateId > 0;

  const detail = useQuery({
    queryKey: affiliateQueryKeys.detail(affiliateId),
    queryFn: () => affiliateAdminApi.get(affiliateId),
    enabled: validId,
  });
  const invalidate = useInvalidateAffiliate(affiliateId);

  return (
    <Box sx={{ maxWidth: 1280 }}>
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link component={RouterLink} to="/admin/affiliates">
          Affiliates
        </Link>
        <Typography color="text.primary">
          {detail.data?.name ?? "Affiliate"}
        </Typography>
      </Breadcrumbs>

      {!validId && (
        <Alert severity="error">This affiliate link is invalid.</Alert>
      )}
      {detail.isLoading && <CircularProgress aria-label="Loading affiliate" />}
      {detail.isError && (
        <Alert severity="error" role="alert">
          {apiErrorMessage(detail.error, "This affiliate could not be loaded.")}
        </Alert>
      )}

      {detail.data && (
        <>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h4" component="h1">
              {detail.data.name}
            </Typography>
            <Typography color="text.secondary">
              Slug <code>{detail.data.slug}</code> · preview any time at{" "}
              <code>/login?affiliate={detail.data.slug}</code>
            </Typography>
          </Box>
          <StatusSection detail={detail.data} />
          <SettingsSection
            key={`${detail.data.name}:${detail.data.hostname ?? ""}`}
            detail={detail.data}
          />
          <BrandingEditor detail={detail.data} onPublished={invalidate} />
        </>
      )}
    </Box>
  );
}
